import {useState, useCallback, useRef, useMemo} from 'react';
import {getDocuments} from '../services/documentService.js';
import {prepareDocStatus} from '../utils/utils.js';

const MAX_CACHE_SIZE = 50; // Limit cache size to prevent memory leaks

export const useDocumentData = accessToken => {
  const [documents, setDocuments] = useState([]);
  const [cache, setCache] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const currentRequestIdRef = useRef(0);

  const formatDocuments = useCallback(async data => {
    if (!data || !data.Items || data.Items.length === 0) return [];

    try {
      const formatted = data.Items.map(item => {
        const doc = JSON.parse(item.Data);
        const metadata = item.Metadata;

        if (
          Array.isArray(doc.Images) &&
          doc.Images.length > 0 &&
          Array.isArray(metadata)
        ) {
          const remainingMetadata = [...metadata];

          doc.Images.forEach(image => {
            if (image?.DocumentSide) {
              const key =
                image.DocumentSide.trim().charAt(0).toUpperCase() +
                image.DocumentSide.trim().slice(1).toLowerCase();

              const index = remainingMetadata.findIndex(
                meta => meta.Item1 === key
              );
              if (index !== -1) {
                image.uri = remainingMetadata[index].Item2;
                remainingMetadata.splice(index, 1);
              } else {
                image.uri = null;
              }
            }
          });
        }

        return {
          country: doc.CountryCode,
          type: doc.DocumentType,
          documentVersion: doc.DocumentVersion,
          state: doc.State,
          supportedPages: prepareDocStatus(doc.Status),
          id: doc.Id,
          images: doc.Images,
          localLanguage: doc?.Attributes?.LocalLanguage,
          yearOfIssue: doc?.Attributes?.YearOfIssue || null,
          supportedFrom: doc.Attributes?.SupportedFrom,
        };
      });

      return formatted.sort((a, b) =>
        (a.country || '').localeCompare(b.country || '')
      );
    } catch (error) {
      console.error('Error formatting documents', error);
      return [];
    }
  }, []);

  const manageCacheSize = useCallback(newCache => {
    const cacheKeys = Object.keys(newCache);
    if (cacheKeys.length > MAX_CACHE_SIZE) {
      const sortedKeys = cacheKeys.sort(
        (a, b) =>
          (newCache[b].lastAccessed || 0) - (newCache[a].lastAccessed || 0)
      );
      const keysToKeep = sortedKeys.slice(0, MAX_CACHE_SIZE);
      const trimmedCache = {};
      keysToKeep.forEach(key => {
        trimmedCache[key] = newCache[key];
      });
      return trimmedCache;
    }
    return newCache;
  }, []);

  const loadDocuments = useCallback(
    async (
      filterData,
      pageNum,
      pageSize,
      queryStringified,
      forceReload = false
    ) => {
      const currentRequestId = ++currentRequestIdRef.current;
      const cacheKey = `${pageNum}-${pageSize}-${JSON.stringify(filterData)}`;

      try {
        setIsLoading(true);
        setError(null);

        // Check cache first
        if (!forceReload && cache[cacheKey]) {
          const cachedData = cache[cacheKey];
          setDocuments([...cachedData.documents]);

          // Update last accessed time
          setCache(prevCache => ({
            ...prevCache,
            [cacheKey]: {...cachedData, lastAccessed: Date.now()},
          }));

          setIsLoading(false);
          return {
            documents: cachedData.documents,
            totalCount: cachedData.totalCount,
          };
        }

        const response = await getDocuments(
          pageNum,
          pageSize,
          filterData,
          accessToken,
          queryStringified
        );

        // Check if request is still current
        if (currentRequestId !== currentRequestIdRef.current) return null;

        if (!response) {
          setDocuments([]);
          setIsLoading(false);
          return {documents: [], totalCount: 0};
        }

        const formattedDocuments = await formatDocuments(response);

        setDocuments([...formattedDocuments]);

        // Update cache with size management
        setCache(prevCache => {
          const newCache = {
            ...prevCache,
            [cacheKey]: {
              documents: formattedDocuments,
              totalCount: response?.TotalCount || 0,
              lastAccessed: Date.now(),
            },
          };
          return manageCacheSize(newCache);
        });

        window.scrollTo({top: 0, behavior: 'smooth'});
        setIsLoading(false);

        return {
          documents: formattedDocuments,
          totalCount: response?.TotalCount || 0,
        };
      } catch (error) {
        if (currentRequestId !== currentRequestIdRef.current) return null;

        const errorMessage =
          error?.message || 'Unknown error occurred while loading documents.';
        setError(errorMessage);
        console.error('Error in loadDocuments:', error);
        setDocuments([]);
        setIsLoading(false);
        throw error;
      }
    },
    [accessToken, cache, formatDocuments, manageCacheSize]
  );

  const clearCache = useCallback(() => {
    setCache({});
  }, []);

  const sortedDocuments = useMemo(() => {
    return [...documents].sort((a, b) =>
      (a.country || '').localeCompare(b.country || '')
    );
  }, [documents]);

  return {
    documents: sortedDocuments,
    isLoading,
    error,
    setError,
    loadDocuments,
    clearCache,
  };
};
